// Pure calculation helpers for EVERMIN Smart Calculators.

export function calcEmi({ principal, annualRate, years }) {
  const P = Number(principal) || 0;
  const n = (Number(years) || 0) * 12;
  const r = (Number(annualRate) || 0) / 12 / 100;
  if (P <= 0 || n <= 0) return { emi: 0, totalInterest: 0, totalPayment: 0, principal: P };
  let emi;
  if (r === 0) emi = P / n;
  else emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const totalPayment = emi * n;
  return {
    emi: Math.round(emi),
    totalInterest: Math.round(totalPayment - P),
    totalPayment: Math.round(totalPayment),
    principal: P,
  };
}

export function calcBudget({ income, expenses, emi, savingsTarget }) {
  const inc = Number(income) || 0;
  const list = expenses || [];
  const fixed = list.filter((e) => e.type !== "variable").reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const variable = list.filter((e) => e.type === "variable").reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const debt = Number(emi) || 0;
  const totalExpenses = fixed + variable + debt;
  const savings = inc - totalExpenses;
  const savingsRate = inc > 0 ? Math.round((savings / inc) * 100) : 0;
  const target = Number(savingsTarget) || 0;
  const vsTarget = savings - target;
  const breakdown = list
    .map((e) => ({ name: e.name, amount: Number(e.amount) || 0, pct: inc > 0 ? Math.round(((Number(e.amount) || 0) / inc) * 100) : 0 }))
    .filter((e) => e.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  return { income: inc, fixed, variable, debt, totalExpenses, savings, savingsRate, target, vsTarget, breakdown };
}

export function calcRentVsBuy({
  monthlyRent, rentIncrease, homePrice, downPayment, loanRate, loanYears, maintenance, years,
  appreciation, opportunityReturn,
}) {
  const horizon = Number(years) || 0;
  let totalRent = 0;
  let rent = Number(monthlyRent) || 0;
  const inc = (Number(rentIncrease) || 0) / 100;
  for (let y = 0; y < horizon; y++) { totalRent += rent * 12; rent = rent * (1 + inc); }

  const dp = Number(downPayment) || 0;
  const price = Number(homePrice) || 0;
  const principal = Math.max(price - dp, 0);
  const { emi } = calcEmi({ principal, annualRate: loanRate, years: loanYears });
  const emiMonths = Math.min(horizon, Number(loanYears) || 0) * 12;
  const totalEmi = emi * emiMonths;
  const totalMaintenance = (Number(maintenance) || 0) * 12 * horizon;
  const grossBuy = dp + totalEmi + totalMaintenance;

  // Property appreciation and loan payoff → equity you'd hold at the horizon.
  const appr = (Number(appreciation) || 0) / 100;
  const homeFuture = price * Math.pow(1 + appr, horizon);
  const r = (Number(loanRate) || 0) / 12 / 100;
  let bal = principal;
  for (let m = 0; m < emiMonths; m++) { const i = bal * r; bal = bal - (emi - i); if (bal <= 0) { bal = 0; break; } }
  const equity = Math.max(0, homeFuture - bal);
  const netBuy = Math.round(grossBuy - equity);

  // Opportunity cost: a renter keeps the down payment invested for the horizon.
  const oppR = (Number(opportunityReturn) || 0) / 100;
  const oppGain = dp * Math.pow(1 + oppR, horizon) - dp;
  const netRent = Math.round(totalRent - oppGain);

  return {
    totalRent: Math.round(totalRent),
    totalBuy: Math.round(grossBuy),
    emi: Math.round(emi),
    homeFuture: Math.round(homeFuture),
    equity: Math.round(equity),
    oppGain: Math.round(oppGain),
    netRent, netBuy,
    cheaper: netRent === netBuy ? "equal" : netRent < netBuy ? "rent" : "buy",
    difference: Math.round(Math.abs(netRent - netBuy)),
  };
}

export function formatCurrency(n, language = "en") {
  const value = Math.round(Number(n) || 0);
  try {
    return new Intl.NumberFormat(language === "hi" ? "hi-IN" : "en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return "₹" + value.toLocaleString("en-IN");
  }
}

const num = (x) => Number(x) || 0;
const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

export function calcSimpleInterest({ principal, rate, years }) {
  const P = num(principal), r = num(rate), t = num(years);
  const interest = (P * r * t) / 100;
  return { interest: Math.round(interest), total: Math.round(P + interest), principal: P };
}

export function calcCompoundInterest({ principal, rate, years, timesPerYear, monthly, inflation }) {
  const P = num(principal), r = num(rate) / 100, t = num(years), n = num(timesPerYear) || 1;
  const M = num(monthly), infl = num(inflation) / 100;
  const lump = n > 0 ? P * Math.pow(1 + r / n, n * t) : P;
  const rm = Math.pow(1 + r / n, n / 12) - 1; // effective monthly rate
  const months = Math.round(t * 12);
  const fvContrib = rm === 0 ? M * months : M * ((Math.pow(1 + rm, months) - 1) / rm);
  const total = Math.round(lump + fvContrib);
  const invested = Math.round(P + M * months);
  const interest = Math.max(0, total - invested);
  const realValue = Math.round(total / Math.pow(1 + infl, t));
  return { interest, total, principal: P, invested, realValue };
}

export function calcPercentage({ value, percent }) {
  const result = (num(value) * num(percent)) / 100;
  return { result: round2(result), value: num(value), percent: num(percent) };
}

export function calcPercentageChange({ oldValue, newValue }) {
  const o = num(oldValue), nv = num(newValue);
  const change = o === 0 ? 0 : ((nv - o) / Math.abs(o)) * 100;
  return { change: round2(change), difference: round2(nv - o), direction: nv >= o ? "up" : "down" };
}

export function calcDiscount({ price, discountPercent }) {
  const p = num(price), d = num(discountPercent);
  const saved = (p * d) / 100;
  return { saved: Math.round(saved), final: Math.round(p - saved), price: p };
}

export function calcGst({ amount, gstPercent, inclusive }) {
  const a = num(amount), g = num(gstPercent);
  if (inclusive) {
    const base = a / (1 + g / 100);
    return { base: Math.round(base), gst: Math.round(a - base), total: Math.round(a) };
  }
  const gst = (a * g) / 100;
  return { base: Math.round(a), gst: Math.round(gst), total: Math.round(a + gst) };
}

export function calcSavingsGoal({ goal, current, monthly }) {
  const g = num(goal), c = num(current), m = num(monthly);
  const remaining = Math.max(g - c, 0);
  const months = m > 0 ? Math.ceil(remaining / m) : 0;
  return { remaining: Math.round(remaining), months, years: round2(months / 12) };
}

export function calcUnitPrice({ priceA, qtyA, priceB, qtyB }) {
  const ua = num(qtyA) > 0 ? num(priceA) / num(qtyA) : 0;
  const ub = num(qtyB) > 0 ? num(priceB) / num(qtyB) : 0;
  let cheaper = "equal";
  if (ua && ub) cheaper = ua < ub ? "a" : ua > ub ? "b" : "equal";
  return { unitA: round2(ua), unitB: round2(ub), cheaper };
}

export function calcPriceDifference({ priceA, priceB }) {
  const a = num(priceA), b = num(priceB);
  const diff = Math.abs(a - b);
  const pct = Math.min(a, b) > 0 ? (diff / Math.min(a, b)) * 100 : 0;
  return { difference: Math.round(diff), percent: round2(pct), cheaper: a === b ? "equal" : a < b ? "a" : "b" };
}

export function calcMonthlyAnnual({ amount, period }) {
  const a = num(amount);
  const monthly = period === "annual" ? a / 12 : a;
  return { monthly: Math.round(monthly), annual: Math.round(monthly * 12) };
}

export function calcAge({ dob }) {
  if (!dob) return { years: 0, months: 0, days: 0 };
  const d = new Date(dob), now = new Date();
  if (isNaN(d) || d > now) return { years: 0, months: 0, days: 0 };
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  let days = now.getDate() - d.getDate();
  if (days < 0) { months -= 1; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
  if (months < 0) { years -= 1; months += 12; }
  const totalDays = Math.floor((now - d) / 86400000);
  return { years, months, days, totalDays };
}

export function calcDateDifference({ from, to }) {
  if (!from || !to) return { days: 0, weeks: 0, months: 0 };
  const a = new Date(from), b = new Date(to);
  if (isNaN(a) || isNaN(b)) return { days: 0, weeks: 0, months: 0 };
  const days = Math.abs(Math.round((b - a) / 86400000));
  return { days, weeks: round2(days / 7), months: round2(days / 30.44) };
}

export function calcTimeDifference({ start, end }) {
  if (!start || !end) return { hours: 0, minutes: 0, totalMinutes: 0 };
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let total = (eh * 60 + em) - (sh * 60 + sm);
  if (total < 0) total += 24 * 60;
  return { hours: Math.floor(total / 60), minutes: total % 60, totalMinutes: total };
}

export function calcSplitBill({ total, people, tipPercent }) {
  const t = num(total), p = num(people), tip = num(tipPercent);
  const grand = t * (1 + tip / 100);
  return { grand: Math.round(grand), perPerson: p > 0 ? Math.round(grand / p) : 0, tipAmount: Math.round(t * tip / 100) };
}

export function calcFuelCost({ distance, mileage, fuelPrice }) {
  const d = num(distance), m = num(mileage), f = num(fuelPrice);
  const litres = m > 0 ? d / m : 0;
  return { litres: round2(litres), cost: Math.round(litres * f) };
}

export function calcMileage({ distance, litres }) {
  const d = num(distance), l = num(litres);
  return { mileage: l > 0 ? round2(d / l) : 0 };
}

export function calcTripCost({ distance, mileage, fuelPrice, tolls, other }) {
  const fuel = calcFuelCost({ distance, mileage, fuelPrice });
  const total = fuel.cost + num(tolls) + num(other);
  return { litres: fuel.litres, fuelCost: fuel.cost, total: Math.round(total) };
}

// ---------- Advanced planners ----------

// Smart Savings Goal: FV of current + monthly SIP, required monthly, months to goal,
// inflation-adjusted goal value and year-wise progress.
export function calcSavingsGoalPlan({ goal, current, monthly, annualReturn, months, inflation }) {
  const G = num(goal), C = num(current), M = num(monthly);
  const r = num(annualReturn) / 100 / 12;
  const n = Math.max(0, Math.round(num(months)));
  const infl = num(inflation) / 100;

  const fvCurrent = C * Math.pow(1 + r, n);
  const fvSip = r === 0 ? M * n : M * ((Math.pow(1 + r, n) - 1) / r);
  const projected = Math.round(fvCurrent + fvSip);
  const shortfall = Math.round(G - projected); // positive = behind

  const remainder = G - fvCurrent;
  let requiredMonthly = 0;
  if (n > 0 && remainder > 0) {
    requiredMonthly = r === 0 ? remainder / n : (remainder * r) / (Math.pow(1 + r, n) - 1);
  }
  requiredMonthly = Math.round(Math.max(0, requiredMonthly));
  const extraMonthly = Math.max(0, requiredMonthly - M);

  let monthsToGoal = 0;
  if (G > C) {
    let bal = C, m = 0;
    while (bal < G && m < 1200) { bal = bal * (1 + r) + M; m++; }
    monthsToGoal = bal >= G ? m : null; // null = never with current inputs
  }

  const totalContrib = Math.round(C + M * n);
  const returns = Math.max(0, projected - totalContrib);
  const realValueOfGoal = Math.round(G / Math.pow(1 + infl, n / 12));

  const series = [];
  let bal = C;
  for (let m = 1; m <= n; m++) {
    bal = bal * (1 + r) + M;
    if (m % 12 === 0 || m === n) series.push({ month: m, year: Math.ceil(m / 12), value: Math.round(bal), invested: Math.round(C + M * m) });
  }
  return {
    projected, shortfall, requiredMonthly, extraMonthly, monthsToGoal,
    totalContrib, returns, realValueOfGoal, series, onTrack: projected >= G,
  };
}

// Advanced Investment: lump sum + monthly SIP with annual step-up, inflation-adjusted.
export function calcInvestment({ lumpsum, monthly, years, annualReturn, stepUp, inflation }) {
  const L = num(lumpsum), M0 = num(monthly);
  const Y = Math.max(0, Math.round(num(years)));
  const r = num(annualReturn) / 100 / 12;
  const su = num(stepUp) / 100;
  const infl = num(inflation) / 100;

  let bal = L, invested = L, M = M0;
  const series = [];
  for (let y = 1; y <= Y; y++) {
    for (let mo = 0; mo < 12; mo++) { bal = bal * (1 + r) + M; invested += M; }
    series.push({ year: y, value: Math.round(bal), invested: Math.round(invested) });
    M = M * (1 + su);
  }
  const corpus = Math.round(bal);
  const totalInvested = Math.round(invested);
  const returns = Math.max(0, corpus - totalInvested);
  const realValue = Math.round(corpus / Math.pow(1 + infl, Y));
  return { corpus, totalInvested, returns, realValue, series };
}

// EMI with optional extra monthly prepayment → payoff time, interest saved, amortization.
export function calcEmiPlan({ principal, annualRate, years, prepayment }) {
  const base = calcEmi({ principal, annualRate, years });
  const P = num(principal), r = num(annualRate) / 12 / 100, emi = base.emi, extra = num(prepayment);
  let bal = P, months = 0, interestPaid = 0;
  const yearly = [];
  let yrInterest = 0, yrPrincipal = 0;
  if (emi > 0) {
    while (bal > 0.5 && months < 1200) {
      const i = bal * r;
      let principalComp = emi - i + extra;
      if (principalComp <= 0) break; // EMI can't cover interest
      principalComp = Math.min(bal, principalComp);
      bal -= principalComp; interestPaid += i; months++;
      yrInterest += i; yrPrincipal += principalComp;
      if (months % 12 === 0) { yearly.push({ year: months / 12, interest: Math.round(yrInterest), principal: Math.round(yrPrincipal), balance: Math.max(0, Math.round(bal)) }); yrInterest = 0; yrPrincipal = 0; }
    }
  }
  if (yrInterest > 0 || yrPrincipal > 0) yearly.push({ year: Math.ceil(months / 12), interest: Math.round(yrInterest), principal: Math.round(yrPrincipal), balance: Math.max(0, Math.round(bal)) });
  const totalInterestWithPrepay = Math.round(interestPaid);
  const savedInterest = Math.max(0, base.totalInterest - totalInterestWithPrepay);
  const monthsSaved = Math.max(0, (num(years) * 12) - months);
  return { ...base, prepayment: extra, payoffMonths: months, totalInterestWithPrepay, savedInterest, monthsSaved, yearly };
}
